import React, { FC, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { makeStyles, Grid } from '@material-ui/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';

import { store } from '../../store';
import { convertWSAlertToAlert } from './utils';

import apiConstants from '../../constants/apiConstants';
import LocalStorageService from '../../services/LocalStorageService';

import { Dispatch, iRootState } from '../../store';

import Item from './Item';
import useSound from 'use-sound';
import alertSound from '../../assets/sounds/alert.wav';
import UsersService from '../../services/UsersService';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    position: 'fixed',
    top: 78,
    right: 24,
    width: 380,
    zIndex: 1200,
    '& > *:not(:last-child)': {
      marginBottom: spacing(1)
    }
  }
}));

const mapState = (state: iRootState) => ({
  alerts: state.alerts.list,
  soundEnabled: state.alerts.soundEnabled,
  isAuth: state.auth.isAuth
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getAlerts: d.alerts.getAlerts
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = connectedProps;

const Alerts: FC<IProps> = ({ getAlerts, alerts, soundEnabled, isAuth }) => {
  const classes = useStyles();

  const VITE_DEV_URL = import.meta.env.VITE_DEV_URL;
  const { Disconnected } = HubConnectionState;
  const url = `${VITE_DEV_URL || ''}${apiConstants.prefix}`;

  const connectionRef = useRef<HubConnection | null>(null);

  const [play, { stop }] = useSound(alertSound, {
    loop: 1,
    interrupt: true,
    volume: Number(soundEnabled)
  });

  const listSize = 3;

  const subscribePlatform = async () => {
    const conn = connectionRef.current;
    if (!conn) return;
    try {
      await conn.invoke('SubscribePlatform');
    } catch (err: any) {
      if (
        err &&
        (err.message === 'Unauthorized' ||
          String(err).toLowerCase().includes('unauthorized'))
      ) {
        const { refreshToken } = LocalStorageService.getToken();
        if (refreshToken) {
          try {
            const { data } = await UsersService.getRefreshedToken(refreshToken);
            LocalStorageService.setToken(data);
            await conn.stop();
            await conn.start();
            await conn.invoke('SubscribePlatform');
          } catch {
            LocalStorageService.clearToken();
            window.location.reload();
          }
        }
      }
    }
  };


  const signalRStart = async () => {
    let conn = connectionRef.current;
    if (!conn) {
      conn = new HubConnectionBuilder()
        .withUrl(`${url}hubs/notifications`, {
          accessTokenFactory: () => {
            const { accessToken } = LocalStorageService.getToken();
            return accessToken || '';
          }
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: () => {
            return 5000;
          }
        })
        .build();
      connectionRef.current = conn;
    }

    if (conn.state === Disconnected) {
      await conn.start();
      await subscribePlatform();

      // предотвращаем дублирование обработчика при повторных стартах
      conn.off('OnNotificationMessage');
      conn.on('OnNotificationMessage', payload => {
        const alert = convertWSAlertToAlert(payload);
        // tslint:disable-next-line:no-console
        console.log(payload);

        store.dispatch.alerts.addAlert(alert);
      });
      conn.onreconnected(async () => {
        getAlerts();
        await subscribePlatform();
      });
      conn.onclose(async error => {
        // tslint:disable-next-line:no-console
        console.log(error);
        if (error && error.message === 'Unauthorized') {
          const { refreshToken } = LocalStorageService.getToken();

          if (refreshToken) {
            await UsersService.getRefreshedToken(refreshToken)
              .then(({ data }) => {
                LocalStorageService.setToken(data);
                signalRStart();
              })
              .catch(() => {
                LocalStorageService.clearToken();
                window.location.reload();
              });
          }
        }
      });
    }
  };

  const signalRStop = async () => {
    const conn = connectionRef.current;
    if (conn) {
      try {
        conn.off('OnNotificationMessage');
        await conn.stop();
      } catch (e) {
        // tslint:disable-next-line:no-console
        console.error(e);
      } finally {
        connectionRef.current = null;
      }
    }
  };

  useEffect(() => {
    getAlerts();
    signalRStart();

    return () => {
      void signalRStop();
    };
  }, []);

  return (
    <Grid className={classes.root}>
      {alerts.slice(0, listSize).map(alert => (
        <Item key={alert.uid} alert={alert} play={play} stop={stop} />
      ))}
    </Grid>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Alerts);
